import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { FlaskConical, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';

const WATERFALL_COLORS = {
  base: '#0A2463',
  cost: '#007AFF',
  total: '#16A34A',
  margin: '#E11D48',
};

export default function MoleculeCostPage() {
  const [movements, setMovements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [moleculeCost, setMoleculeCost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/movements').then(r => {
      setMovements(r.data);
      if (r.data.length > 0) loadMolecule(r.data[0].id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadMolecule = async (movId) => {
    setLoading(true);
    setSelected(movId);
    try {
      const { data } = await api.get(`/movements/${movId}/molecule-cost`);
      setMoleculeCost(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const waterfallData = moleculeCost ? [
    { name: 'Base Price', value: moleculeCost.base_price, color: WATERFALL_COLORS.base },
    ...moleculeCost.cost_breakdown.map(c => ({ name: c.type.replace(/_/g, ' '), value: c.amount, color: WATERFALL_COLORS.cost })),
    { name: 'All-In Cost', value: moleculeCost.true_all_in_cost, color: WATERFALL_COLORS.total },
  ] : [];

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="molecule-cost-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Molecule Cost Breakdown</h1>
          <p className="text-sm text-slate-500 mt-1">True all-in cost per molecule for each cargo movement</p>
        </div>
        <Select value={selected || ''} onValueChange={loadMolecule}>
          <SelectTrigger className="w-64 rounded-sm border-slate-200" data-testid="movement-selector">
            <SelectValue placeholder="Select Movement" />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            {movements.map(m => (
              <SelectItem key={m.id} value={m.id}>
                {m.trade_id} — {m.vessel_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            {[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      ) : !moleculeCost ? (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="py-24 text-center">
            <FlaskConical className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
            <p className="mt-4 text-sm text-slate-400">Select a movement to view molecule cost breakdown</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Margin Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Apparent Margin</p>
                <p className="font-mono text-2xl font-bold text-slate-900 mt-1">${moleculeCost.apparent_margin?.toLocaleString()}</p>
                <p className="text-xs text-slate-400 mt-1">Purchase + Sale only</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">True Margin</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className={`font-mono text-2xl font-bold ${moleculeCost.true_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${Math.abs(moleculeCost.true_margin)?.toLocaleString()}
                  </p>
                  {moleculeCost.true_margin >= 0 ? <TrendingUp className="h-5 w-5 text-green-500" /> : <TrendingDown className="h-5 w-5 text-red-500" />}
                </div>
                <p className="text-xs text-slate-400 mt-1">After all secondary costs</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Total Secondary Costs</p>
                <p className="font-mono text-2xl font-bold text-[#007AFF] mt-1">${moleculeCost.total_secondary_costs?.toLocaleString()}</p>
                <p className="text-xs text-slate-400 mt-1">{moleculeCost.cost_breakdown?.length} cost types</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Pending Costs</p>
                <p className="font-mono text-2xl font-bold text-amber-600 mt-1">{moleculeCost.pending_costs || 0}</p>
                {moleculeCost.pending_costs > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <AlertTriangle className="h-3 w-3 text-amber-500" />
                    <p className="text-xs text-amber-600">Awaiting confirmation</p>
                  </div>
                )}
                {!moleculeCost.pending_costs && <p className="text-xs text-slate-400 mt-1">All costs confirmed</p>}
              </CardContent>
            </Card>
          </div>

          {/* Waterfall Chart */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800">Cost Waterfall</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={waterfallData} margin={{ left: 20, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} angle={-30} textAnchor="end" />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                    <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={(v) => `$${v.toLocaleString()}`} />
                    <Bar dataKey="value" radius={[2, 2, 0, 0]} barSize={32}>
                      {waterfallData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Cost Table */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800">Cost Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="cost-breakdown-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Cost Type</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Amount</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <td className="px-5 py-3 font-semibold text-slate-800">Base Price</td>
                    <td className="px-5 py-3 text-right font-mono text-slate-900">${moleculeCost.base_price?.toLocaleString()}</td>
                    <td className="px-5 py-3 text-right font-mono text-slate-400">—</td>
                  </tr>
                  {moleculeCost.cost_breakdown?.map(c => (
                    <tr key={c.type} className="border-b border-slate-50">
                      <td className="px-5 py-3">
                        <Badge variant="outline" className="rounded-sm text-[10px] uppercase">{c.type.replace(/_/g, ' ')}</Badge>
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-slate-900">${c.amount?.toLocaleString()}</td>
                      <td className="px-5 py-3 text-right font-mono text-slate-500">
                        {moleculeCost.total_secondary_costs > 0 ? `${(c.amount / moleculeCost.total_secondary_costs * 100).toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50/80">
                    <td className="px-5 py-3 font-bold text-slate-900">True All-In Cost</td>
                    <td className="px-5 py-3 text-right font-mono font-bold text-slate-900">${moleculeCost.true_all_in_cost?.toLocaleString()}</td>
                    <td className="px-5 py-3"></td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
